// Dashboard — Phase 4 Plan 04-05 (DIS-14, DIS-15, DIS-17, UI-SPEC §Screen 1).
//
// Single most visible Phase 4 surface. Dispatchers live here during the day:
//   - KPI row (Active / Problems / On-time / Late) tap → filtered trips list.
//   - DashboardMap with HERE Maps clustering + client-side color FSM.
//   - Alerts panel with manual-dismiss AlertToasts (D-74 alerts).
//
// Real-time updates: EventSource opens /api/v1/dashboard/events on mount and
// merges live `gps_update` + `alert` payloads into local state. Last-Event-ID
// is persisted to localStorage so brief disconnects resync without losing
// status changes.

import { useQuery } from "@tanstack/react-query";
import { Badge, Banner, Card, Section } from "@telegram-apps/telegram-ui";
import { useEffect, useMemo, useReducer, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  type DashboardSseEvent,
  type DashboardState,
  type DashboardTrip,
  dashboardApi,
} from "../api/dashboard";
import { useAuth } from "../auth/useAuth";
import { AlertToast } from "../components/AlertToast";
import { DashboardMap } from "../components/DashboardMap";
import { KpiCard } from "../components/KpiCard";

const LAST_EVENT_ID_KEY = "dashboard.lastEventId";

// ---------------------------------------------------------------------------
// Local state reducer — merges SSE deltas into the snapshot.
// ---------------------------------------------------------------------------

interface UiAlert {
  id: string;
  alertType: "driver_offline_1h" | "overdue_2h" | "problem_open";
  severity: "warning" | "destructive";
  tripId: number;
  driverId: number | null;
}

interface DashboardLocalState {
  trips: DashboardTrip[];
  alerts: UiAlert[];
  seenEventIds: Set<string>;
}

type Action =
  | { kind: "hydrate"; snapshot: DashboardState }
  | {
      kind: "gps_update";
      tripId: number;
      lat: number;
      lng: number;
      recordedAt: string;
      eventId: string;
    }
  | { kind: "alert"; alert: UiAlert; eventId: string }
  | { kind: "dismiss"; alertId: string };

function reducer(state: DashboardLocalState, action: Action): DashboardLocalState {
  switch (action.kind) {
    case "hydrate":
      return {
        trips: action.snapshot.trips,
        alerts: state.alerts, // keep any live alerts that arrived first
        seenEventIds: new Set(),
      };
    case "gps_update": {
      if (state.seenEventIds.has(action.eventId)) return state;
      const nextSeen = new Set(state.seenEventIds);
      nextSeen.add(action.eventId);
      const trips = state.trips.map((t) => {
        if (t.id !== action.tripId) return t;
        return {
          ...t,
          last_gps: {
            lat: action.lat,
            lng: action.lng,
            recorded_at: action.recordedAt,
          },
        };
      });
      return { ...state, trips, seenEventIds: nextSeen };
    }
    case "alert": {
      if (state.seenEventIds.has(action.eventId)) return state;
      const nextSeen = new Set(state.seenEventIds);
      nextSeen.add(action.eventId);
      // De-dup by alert.id too — re-renders should not push duplicates.
      if (state.alerts.some((a) => a.id === action.alert.id)) {
        return { ...state, seenEventIds: nextSeen };
      }
      return {
        ...state,
        alerts: [action.alert, ...state.alerts].slice(0, 50),
        seenEventIds: nextSeen,
      };
    }
    case "dismiss": {
      return {
        ...state,
        alerts: state.alerts.filter((a) => a.id !== action.alertId),
      };
    }
    default:
      return state;
  }
}

const INITIAL_STATE: DashboardLocalState = {
  trips: [],
  alerts: [],
  seenEventIds: new Set(),
};

// ---------------------------------------------------------------------------
// Dashboard screen
// ---------------------------------------------------------------------------

export interface DashboardProps {
  baseUrl: string;
  /** HERE Maps API key. Optional for tests; production reads from env. */
  hereApiKey?: string;
  /** Optional EventSource factory override — test seam (vitest jsdom). */
  eventSourceFactory?: (
    baseUrl: string,
    token: string | null,
    options: { lastEventId?: string | null },
  ) => EventSource;
}

export function Dashboard({
  baseUrl,
  hereApiKey,
  eventSourceFactory,
}: DashboardProps): React.JSX.Element {
  const { t } = useTranslation();
  const { apiClient } = useAuth();
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const esRef = useRef<EventSource | null>(null);

  // CR-05 / CR-12: the JWT is NO LONGER passed through the SSE URL.
  // The EventSource auth flow now goes:
  //   POST /api/v1/dashboard/sse-token (Authorization header carries JWT)
  //   → server returns one-time `ticket` bound to the caller's company.
  //   → EventSource opens with `?ticket=<ticket>` (single-use, 60s TTL,
  //     burned on first use). Even if the ticket leaks to logs/history/
  //     referer it's already invalidated.
  // CR-12 (regression closure): on sseToken() failure we DO NOT fall
  // back to passing the raw JWT — see openEventSource below. The
  // dashboard simply skips opening SSE; snapshot poll keeps the UI
  // alive until the next refetch reattempts the mint.

  // ---- Initial snapshot -------------------------------------------------
  const snapshotQuery = useQuery({
    queryKey: ["dashboard-state"],
    queryFn: () => dashboardApi.state(apiClient),
    staleTime: 30_000,
  });

  // Hydrate local state from snapshot.
  useEffect(() => {
    if (snapshotQuery.data) {
      dispatch({ kind: "hydrate", snapshot: snapshotQuery.data });
    }
  }, [snapshotQuery.data]);

  // ---- SSE connection ---------------------------------------------------
  useEffect(() => {
    // Wait until initial snapshot is fetched before opening SSE — avoids race
    // between hydrate and live gps_update for the same trip.
    if (!snapshotQuery.data) return;

    const factory =
      eventSourceFactory ??
      ((b: string, tok: string | null, opt: { lastEventId?: string | null }) =>
        dashboardApi.events(b, tok, opt));

    // CR-05: mint a one-time SSE ticket via authenticated POST. The
    // EventSource opens with `?ticket=<single-use 256-bit secret>` —
    // never the JWT. If the ticket mint fails, the SSE just doesn't
    // open and the user keeps seeing the last snapshot until the next
    // poll cycle (snapshotQuery has staleTime 30s — see above).
    let cancelled = false;
    const lastEventId = localStorage.getItem(LAST_EVENT_ID_KEY);

    const attachEventHandlers = (es: EventSource): void => {
      es.onmessage = (ev: MessageEvent) => {
        let parsed: DashboardSseEvent | null = null;
        try {
          parsed = JSON.parse(ev.data) as DashboardSseEvent;
        } catch {
          return;
        }
        if (!parsed) return;
        if (ev.lastEventId) {
          localStorage.setItem(LAST_EVENT_ID_KEY, ev.lastEventId);
        }

        switch (parsed.type) {
          case "gps_update":
            dispatch({
              kind: "gps_update",
              eventId: parsed.event_id,
              tripId: parsed.data.trip_id,
              lat: parsed.data.lat,
              lng: parsed.data.lng,
              recordedAt: parsed.data.recorded_at,
            });
            break;
          case "alert":
            dispatch({
              kind: "alert",
              eventId: parsed.event_id,
              alert: {
                id: parsed.event_id,
                alertType: parsed.data.alert_type,
                severity: parsed.data.severity,
                tripId: parsed.data.trip_id,
                driverId: parsed.data.driver_id,
              },
            });
            break;
          // trip_event from resync — no UI delta required for MVP.
          default:
            break;
        }
      };

      es.onerror = (err) => {
        // Browser auto-reconnects EventSource; we just log.
        console.warn("Dashboard SSE error", err);
      };
    };

    const openEventSource = async (): Promise<void> => {
      let ticket: string | null = null;
      try {
        const resp = await dashboardApi.sseToken(apiClient);
        ticket = resp.ticket;
      } catch (err) {
        // CR-12 (regression of CR-05): do NOT fall back to JWT-in-URL.
        // The previous fallback passed the raw JWT as ?ticket=<jwt> on
        // any Redis/network blip — exactly the leak that CR-05 was
        // closing. The JWT would flow into Caddy access logs, browser
        // history, and HERE Maps tile Referer headers.
        //
        // Correct behaviour on mint failure: surface a console warning
        // and do NOT open the EventSource. The dashboard degrades to
        // snapshot-poll mode (snapshotQuery has staleTime 30s, so the
        // next user-driven refetch will reattempt the mint). On
        // intermittent failure the user sees stale but accurate data
        // rather than a logged credential.
        // eslint-disable-next-line no-console
        console.warn("dashboard SSE ticket mint failed; skipping SSE open:", err);
        return;
      }
      if (cancelled) return;
      if (ticket == null) return;
      const es = factory(baseUrl, ticket, { lastEventId });
      esRef.current = es;
      attachEventHandlers(es);
    };
    void openEventSource();

    return () => {
      cancelled = true;
      const es = esRef.current;
      if (es) es.close();
      esRef.current = null;
    };
  }, [snapshotQuery.data, baseUrl, eventSourceFactory, apiClient]);

  // ---- Render -----------------------------------------------------------
  const kpis = snapshotQuery.data?.kpis ?? {
    active: 0,
    problems: 0,
    on_time_pct: null,
    late: 0,
  };

  const onTimeLabel = useMemo(() => {
    if (kpis.on_time_pct == null) return "—";
    return `${kpis.on_time_pct.toFixed(1)}%`;
  }, [kpis.on_time_pct]);

  const dismiss = (id: string) => dispatch({ kind: "dismiss", alertId: id });

  // R4: one screen = live map + color FSM + alert stack. UI-SPEC §7 Wave 2
  // wraps the heavy Dashboard surface in the native telegram-ui shell:
  //   - KPI row → <Card> (tappable KpiCard cells inside);
  //   - HERE Maps → <Card> (integration logic untouched — UI-SPEC §10);
  //   - alert stack → <Banner type="section"> via AlertToast (severity bridge);
  //   - SSE live/offline indicator → <Badge>.
  const sseMode = snapshotQuery.isLoading ? "offline" : "live";

  return (
    <div
      data-testid="dashboard"
      style={{
        padding: 16,
        display: "flex",
        flexDirection: "column",
        gap: 16,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <h2 style={{ margin: 0 }}>{t("dispatcher:dashboard.heading", "Dashboard")}</h2>

        {/* SSE connection indicator → native telegram-ui Badge (live/offline). */}
        <span
          data-testid="dashboard-sse-state"
          data-mode={sseMode}
          aria-live="polite"
          aria-label={
            sseMode === "live"
              ? (t("dispatcher:dashboard.sse_live", "Live") as string)
              : (t("dispatcher:dashboard.sse_offline", "Connecting…") as string)
          }
        >
          <Badge type="dot" mode={sseMode === "live" ? "primary" : "gray"} />
          {sseMode === "live"
            ? (t("dispatcher:dashboard.sse_live", "Live") as string)
            : (t("dispatcher:dashboard.sse_offline", "Connecting…") as string)}
        </span>
      </div>

      {/* KPI row → telegram-ui Card (tappable KpiCard cells inside). */}
      <Card data-testid="dashboard-kpi-card">
        <div
          data-testid="dashboard-kpis"
          style={{
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            padding: 8,
          }}
        >
          <KpiCard
            value={kpis.active}
            label={t("dispatcher:dashboard.kpi_active", "Active") as string}
            onClick={() => navigate("/dispatcher/trips?status=active")}
            data-testid="kpi-active"
          />
          <KpiCard
            value={kpis.problems}
            label={t("dispatcher:dashboard.kpi_problems", "Problems") as string}
            onClick={() => navigate("/dispatcher/trips?status=problem")}
            data-testid="kpi-problems"
          />
          <KpiCard
            value={onTimeLabel}
            label={t("dispatcher:dashboard.kpi_on_time", "On-time") as string}
            data-testid="kpi-on-time"
          />
          <KpiCard
            value={kpis.late}
            label={t("dispatcher:dashboard.kpi_late", "Late") as string}
            onClick={() => navigate("/dispatcher/trips?status=late")}
            data-testid="kpi-late"
          />
        </div>
      </Card>

      {/* Map → telegram-ui Card. HERE init/clustering logic untouched (§10). */}
      <Card data-testid="dashboard-map-card">
        <DashboardMap trips={state.trips} apiKey={hereApiKey} />
      </Card>

      {/* Alerts panel — native Section header + Banner-styled AlertToasts. */}
      <Section
        data-testid="dashboard-alerts"
        header={t("dispatcher:dashboard.alerts_heading", "Recent alerts") as string}
      >
        {state.alerts.length === 0 ? (
          <p
            data-testid="dashboard-alerts-empty"
            style={{ color: "var(--tgui--hint_color, #94A3B8)", fontSize: 14, padding: "0 16px" }}
          >
            {t("dispatcher:dashboard.alerts_empty", "No active alerts.")}
          </p>
        ) : null}
        {/* Alert stack — each alert is a native telegram-ui <Banner> shell
            wrapping AlertToast (which preserves the data-testid / data-severity
            / -dismiss contract that downstream tests rely on). Banners are
            persistent (manual-dismiss); transient toasts would use <Snackbar>. */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "0 16px 8px" }}>
          {state.alerts.map((a) => (
            <Banner key={a.id} type="section" data-banner-severity={a.severity}>
              <AlertToast
                severity={a.severity}
                title={t(
                  `dispatcher:dashboard.alert.${a.alertType}.title`,
                  alertTitleFallback(a.alertType),
                )}
                body={
                  t(`dispatcher:dashboard.alert.${a.alertType}.body`, `Trip #${a.tripId}`, {
                    tripId: a.tripId,
                  }) as string
                }
                actionLabel={t("dispatcher:dashboard.alert_open_trip", "Open trip") as string}
                onAction={() => navigate(`/dispatcher/trips/${a.tripId}`)}
                onDismiss={() => dismiss(a.id)}
                data-testid={`alert-${a.id}`}
              />
            </Banner>
          ))}
        </div>
      </Section>
    </div>
  );
}

function alertTitleFallback(type: string): string {
  switch (type) {
    case "driver_offline_1h":
      return "Driver offline";
    case "overdue_2h":
      return "Trip overdue";
    case "problem_open":
      return "Problem reported";
    default:
      return "Alert";
  }
}
